import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BGrainOverlay, BStripes, BHeader } from '../components/layout';
import { getAuthHeaders } from '../../utils/odotId';
import './BBookmarksPage.css';
import '../components/report/BReportHero.css';
import '../components/report/BDiscoverSection.css';

interface BookmarkedReport {
  reportId: string;
  title: string;
  genreTags?: string[];
  artistCount?: number;
  era?: string;
  location?: string;
  bookmarkedAt: string;
}

interface BookmarkedArtist {
  name: string;
  track?: string;
  sourceReportTitle?: string;
  sourceReportId?: string;
  bookmarkedAt: string;
}

interface Board {
  id: string;
  name: string;
  artistCount: number;
  genreTags: string[];
  createdAt: string;
}

interface SearchHistoryEntry {
  reportId: string;
  queryDisplay: string;
  title?: string;
  genreTags?: string[];
  genreDescription?: string;
  searchedAt: string;
}

const HISTORY_PAGE_SIZE = 10;

/**
 * Format a relative date string
 */
function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function BBookmarksPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<BookmarkedReport[]>([]);
  const [artists, setArtists] = useState<BookmarkedArtist[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryEntry[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilter, setHistoryFilter] = useState<string | null>(null);
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const newBoardInputRef = useRef<HTMLInputElement>(null);

  // Load bookmarks from localStorage
  useEffect(() => {
    const savedReports = localStorage.getItem('b_bookmarked_reports');
    const savedArtists = localStorage.getItem('b_bookmarked_artists');
    const savedBoards = localStorage.getItem('b_boards');

    if (savedReports) setReports(JSON.parse(savedReports));
    if (savedArtists) setArtists(JSON.parse(savedArtists));
    if (savedBoards) setBoards(JSON.parse(savedBoards));
  }, []);

  // Fetch search history from API
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await fetch('/api/v1/research/history', {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setSearchHistory(data.searches || []);
        }
      } catch (err) {
        console.error('Failed to fetch search history:', err);
      }
    };
    fetchHistory();
  }, []);

  const removeReport = (reportId: string) => {
    const updated = reports.filter(r => r.reportId !== reportId);
    setReports(updated);
    localStorage.setItem('b_bookmarked_reports', JSON.stringify(updated));
  };

  const removeArtist = (name: string) => {
    const updated = artists.filter(a => a.name !== name);
    setArtists(updated);
    localStorage.setItem('b_bookmarked_artists', JSON.stringify(updated));
  };

  const startCreatingBoard = () => {
    setIsCreatingBoard(true);
    setNewBoardName('');
    setTimeout(() => newBoardInputRef.current?.focus(), 0);
  };

  const confirmCreateBoard = () => {
    const name = newBoardName.trim();
    if (!name) {
      setIsCreatingBoard(false);
      setNewBoardName('');
      return;
    }

    const newBoard: Board = {
      id: Date.now().toString(),
      name,
      artistCount: 0,
      genreTags: [],
      createdAt: new Date().toISOString(),
    };

    const updated = [...boards, newBoard];
    setBoards(updated);
    localStorage.setItem('b_boards', JSON.stringify(updated));
    setIsCreatingBoard(false);
    setNewBoardName('');
  };

  const cancelCreateBoard = () => {
    setIsCreatingBoard(false);
    setNewBoardName('');
  };

  const deleteBoard = (boardId: string) => {
    const updated = boards.filter(b => b.id !== boardId);
    setBoards(updated);
    localStorage.setItem('b_boards', JSON.stringify(updated));
  };

  const totalCount = reports.length + artists.length + boards.length;

  // Filter completed searches for display
  const completedHistory = searchHistory.filter(
    entry => entry.title && entry.title !== 'Discovering artists...'
  );

  // Apply genre filter if active
  const filteredHistory = historyFilter
    ? completedHistory.filter(entry => entry.genreTags?.includes(historyFilter))
    : completedHistory;

  // Pagination for search history
  const totalHistoryPages = Math.ceil(filteredHistory.length / HISTORY_PAGE_SIZE);
  const paginatedHistory = filteredHistory.slice(
    (historyPage - 1) * HISTORY_PAGE_SIZE,
    historyPage * HISTORY_PAGE_SIZE
  );

  // Handle tag click - filter or clear
  const handleHistoryTagClick = (tag: string) => {
    if (historyFilter === tag) {
      setHistoryFilter(null);
    } else {
      setHistoryFilter(tag);
      setHistoryPage(1); // Reset to page 1 when filtering
    }
  };

  return (
    <div className="b-page b-bookmarks-page">
      <BStripes />
      <BGrainOverlay />
      <BHeader showBack />

      {/* Hero Section - matches BReportHero */}
      <section className="b-report-hero">
        <div className="b-report-hero__ghost" aria-hidden="true">
          STUFF
        </div>

        <div className="b-report-hero__content">
          <div className="b-report-hero__left">
            <p className="b-report-hero__label">
              <span className="b-report-hero__label-line" />
              Your Collection
            </p>
            <h1 className="b-report-hero__title">
              My Stuff
            </h1>
          </div>

          <div className="b-report-hero__right">
            <dl className="b-bookmarks-stats">
              <div className="b-bookmarks-stats__item">
                <dd>{completedHistory.length}</dd>
                <dt>Searches</dt>
              </div>
              <div className="b-bookmarks-stats__item">
                <dd>{reports.length}</dd>
                <dt>Reports</dt>
              </div>
              <div className="b-bookmarks-stats__item">
                <dd>{artists.length}</dd>
                <dt>Artists</dt>
              </div>
              <div className="b-bookmarks-stats__item">
                <dd>{boards.length}</dd>
                <dt>Boards</dt>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Boards Section */}
      <section className="b-discover-section">
        <div className="b-discover-section__header">
          <div>
            <span className="b-section-number">01</span>
            <h2 className="b-section-title">Boards</h2>
          </div>
          <span className="b-discover-section__note">Organize your discoveries</span>
        </div>

        <div className="b-discover-section__discover-panel">
          <div className="b-bookmarks-board-grid">
            {boards.map(board => (
              <article key={board.id} className="b-bookmarks-board-card">
                <div className="b-bookmarks-board-card__actions">
                  <button
                    className="b-bookmarks-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteBoard(board.id);
                    }}
                  >
                    ×
                  </button>
                </div>
                <span className="b-bookmarks-board-card__count">{board.artistCount}</span>
                <h3 className="b-bookmarks-board-card__title">{board.name}</h3>
                <p className="b-bookmarks-board-card__meta">
                  {board.artistCount} artists
                  {board.genreTags.length > 0 && ` · ${board.genreTags.join(', ')}`}
                </p>
              </article>
            ))}

            {isCreatingBoard ? (
              <article className="b-bookmarks-board-card b-bookmarks-board-card--creating">
                <input
                  ref={newBoardInputRef}
                  type="text"
                  className="b-bookmarks-board-card__input"
                  placeholder="Board name..."
                  value={newBoardName}
                  onChange={(e) => setNewBoardName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmCreateBoard();
                    if (e.key === 'Escape') cancelCreateBoard();
                  }}
                  onBlur={confirmCreateBoard}
                />
                <p className="b-bookmarks-board-card__hint">
                  Press Enter to create · Esc to cancel
                </p>
              </article>
            ) : (
              <article className="b-bookmarks-board-card b-bookmarks-board-card--create" onClick={startCreatingBoard}>
                <span className="b-bookmarks-board-card__plus">+</span>
                <span>Create new board</span>
              </article>
            )}
          </div>
        </div>
      </section>

      {/* Artists Section */}
      {artists.length > 0 && (
        <section className="b-discover-section">
          <div className="b-discover-section__header">
            <div>
              <span className="b-section-number">02</span>
              <h2 className="b-section-title">Artists</h2>
            </div>
            <span className="b-discover-section__note">{artists.length} saved</span>
          </div>

          <div className="b-discover-section__discover-panel">
            <div className="b-discover-grid">
              {artists.map(artist => (
                <article key={artist.name} className="b-bookmarks-artist-card">
                  <div className="b-bookmarks-artist-card__actions">
                    <button
                      className="b-bookmarks-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeArtist(artist.name);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <h3 className="b-bookmarks-artist-card__name">{artist.name}</h3>
                  {artist.track && (
                    <p className="b-bookmarks-artist-card__track">"{artist.track}"</p>
                  )}
                  <a
                    href={`https://open.spotify.com/search/${encodeURIComponent(artist.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="b-bookmarks-play-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ▶ Spotify
                  </a>
                  {artist.sourceReportTitle && (
                    <p className="b-bookmarks-artist-card__source">
                      from {artist.sourceReportTitle}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reports Section */}
      {reports.length > 0 && (
        <section className="b-discover-section">
          <div className="b-discover-section__header">
            <div>
              <span className="b-section-number">{artists.length > 0 ? '03' : '02'}</span>
              <h2 className="b-section-title">Reports</h2>
            </div>
            <span className="b-discover-section__note">{reports.length} saved</span>
          </div>

          <div className="b-discover-section__discover-panel">
            <div className="b-discover-grid">
              {reports.map(report => (
                <article
                  key={report.reportId}
                  className="b-bookmarks-report-card"
                  onClick={() => navigate(`/report/${report.reportId}`)}
                >
                  <div className="b-bookmarks-report-card__actions">
                    <button
                      className="b-bookmarks-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeReport(report.reportId);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <h3 className="b-bookmarks-report-card__title">{report.title}</h3>
                  {report.genreTags?.[0] && (
                    <span className="b-bookmarks-report-card__genre">{report.genreTags[0]}</span>
                  )}
                  <p className="b-bookmarks-report-card__meta">
                    {report.artistCount && `${report.artistCount} artists`}
                    {report.era && ` · ${report.era}`}
                    {report.location && ` · ${report.location}`}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Search History Section */}
      {completedHistory.length > 0 && (
        <section className="b-discover-section">
          <div className="b-discover-section__header">
            <div>
              <span className="b-section-number">
                {String(
                  1 + // Boards section is always 01
                  (artists.length > 0 ? 1 : 0) +
                  (reports.length > 0 ? 1 : 0)
                ).padStart(2, '0')}
              </span>
              <h2 className="b-section-title">Search History</h2>
            </div>
            <span className="b-discover-section__note">
              {historyFilter ? `${filteredHistory.length} of ${completedHistory.length}` : `${completedHistory.length} searches`}
            </span>
          </div>

          {/* Active filter chip */}
          {historyFilter && (
            <div className="b-history-filter">
              <button
                className="b-history-filter__chip"
                onClick={() => setHistoryFilter(null)}
              >
                {historyFilter} ✕
              </button>
            </div>
          )}

          <div className="b-discover-section__discover-panel">
            <div className="b-history-list">
              {paginatedHistory.map((entry, idx) => (
                <article
                  key={entry.reportId}
                  className="b-history-item"
                  onClick={() => navigate(`/report/${entry.reportId}`)}
                >
                  <span className="b-history-item__number">
                    {String((historyPage - 1) * HISTORY_PAGE_SIZE + idx + 1).padStart(2, '0')}
                  </span>
                  <div className="b-history-item__content">
                    <h3 className="b-history-item__title">{entry.title || entry.queryDisplay}</h3>
                    {entry.genreDescription && (
                      <p className="b-history-item__desc">{entry.genreDescription}</p>
                    )}
                    {entry.genreTags && entry.genreTags.length > 0 && (
                      <div className="b-history-item__tags">
                        {entry.genreTags.slice(0, 3).map(tag => (
                          <button
                            key={tag}
                            className={`b-history-item__tag ${historyFilter === tag ? 'b-history-item__tag--active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHistoryTagClick(tag);
                            }}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="b-history-item__date">{formatRelativeDate(entry.searchedAt)}</span>
                  <span className="b-history-item__arrow">→</span>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalHistoryPages > 1 && (
              <div className="b-history-pagination">
                <button
                  className="b-history-pagination__btn"
                  onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                  disabled={historyPage === 1}
                >
                  ← Prev
                </button>
                <span className="b-history-pagination__info">
                  Page {historyPage} of {totalHistoryPages}
                </span>
                <button
                  className="b-history-pagination__btn"
                  onClick={() => setHistoryPage(p => Math.min(totalHistoryPages, p + 1))}
                  disabled={historyPage === totalHistoryPages}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Empty state */}
      {totalCount === 0 && completedHistory.length === 0 && (
        <section className="b-discover-section">
          <div className="b-bookmarks-empty">
            <h2>No bookmarks yet</h2>
            <p>Save reports and artists as you discover them.</p>
            <Link to="/" className="b-btn">Start Exploring</Link>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="b-results-footer">
        <Link to="/" className="b-btn">
          New Search
        </Link>
      </footer>
    </div>
  );
}
